import * as React from "react"
import { Button, type ButtonProps } from "@/components/ui/button"
import { useMenuPermission } from "@/hooks"

/**
 * 메뉴별 권한(R/C/U/D/E)에 따라 자식 요소를 노출/숨김 처리하는 게이트 컴포넌트.
 *
 * 사용 예:
 *   <Permission action="create">
 *     <Button onClick={...}>등록</Button>
 *   </Permission>
 *
 *   <Permission action="delete" menuPath="/instructor-recruit">
 *     <Button variant="destructive">삭제</Button>
 *   </Permission>
 *
 * 매칭 규칙은 useMenuPermission 참조 (현재 경로 prefix 매칭).
 * menuPath 를 명시하면 현재 경로 대신 그 경로의 메뉴 권한을 사용한다 (상세 페이지에서 부모 메뉴 권한 참조 시 유용).
 */
export type PermissionAction =
  | "read"
  | "create"
  | "update"
  | "delete"
  | "export"

interface PermissionProps {
  action: PermissionAction
  /** 권한을 조회할 메뉴 경로(미지정 시 현재 location.pathname 으로 prefix 매칭) */
  menuPath?: string
  /** 권한이 없을 때 렌더할 대체 노드 (기본: null) */
  fallback?: React.ReactNode
  children: React.ReactNode
}

const PERM_KEY: Record<
  PermissionAction,
  "canRead" | "canCreate" | "canUpdate" | "canDelete" | "canExport"
> = {
  read: "canRead",
  create: "canCreate",
  update: "canUpdate",
  delete: "canDelete",
  export: "canExport",
}

export function Permission({
  action,
  menuPath,
  fallback = null,
  children,
}: PermissionProps) {
  const perm = useMenuPermission(menuPath)
  const allowed = perm[PERM_KEY[action]]
  if (!allowed) return <>{fallback}</>
  return <>{children}</>
}

/**
 * 권한 게이트가 내장된 Button.
 * 권한이 없으면 아예 렌더되지 않으므로, 단일 버튼 케이스에서 보일러플레이트가 줄어든다.
 *
 * 사용 예:
 *   <PermissionButton action="create" onClick={...}>등록</PermissionButton>
 *   <PermissionButton action="delete" variant="destructive">삭제</PermissionButton>
 *
 * 추가로 disabled 가 필요한 비즈니스 조건은 button 의 표준 prop 으로 그대로 결합 가능.
 */
interface PermissionButtonProps extends ButtonProps {
  action: PermissionAction
  menuPath?: string
  /** 권한이 없을 때 표시할 대체 노드 (기본: null = 미렌더) */
  permissionFallback?: React.ReactNode
}

export const PermissionButton = React.forwardRef<
  HTMLButtonElement,
  PermissionButtonProps
>(({ action, menuPath, permissionFallback = null, ...buttonProps }, ref) => {
  const perm = useMenuPermission(menuPath)
  const allowed = perm[PERM_KEY[action]]
  if (!allowed) return <>{permissionFallback}</>
  return <Button ref={ref} {...buttonProps} />
})
PermissionButton.displayName = "PermissionButton"
